/**
 * personalSubscriptionsModel — pure shape + arithmetic for the Personal space's
 * cancellation ledger (`personalSubscriptions`).
 *
 * WHAT A ROW IS. One subscription Anthony (or Grace) has cancelled: a name, what
 * it cost, and whether that cost was per month or per year. The ledger exists
 * to answer one question — "how much are we saving now?" — so the only real
 * computation here is the monthly equivalent and its rollup.
 *
 * ITS OWN COLLECTION, NOT A TASK. Decided 2026-07-31 (handoff
 * `docs/handoffs/personal-subscriptions-tracker-code-handoff-2026-07-31.md`):
 * a cancellation has no lane, status, priority, assignee or due date, and
 * putting it in the task list is the clutter the ledger was split out to
 * remove. The collection is the wall, exactly as `personalTasks` and
 * `personalDocs` are.
 *
 * PURE, LIKE spaceItemsModel. No DOM, no Firestore — the `/12` conversion and
 * the rollup are the part that must be right, and they are testable only if
 * they live outside the component.
 *
 * MONEY IS KEPT IN DOLLARS AS A NUMBER, not cents. These are hand-typed
 * figures ("69.99") on a household ledger, never charged or reconciled, so the
 * cents-integer discipline the billing code uses buys nothing here. Rounding
 * happens once, at display time, in `formatMoney`.
 */

export const PERIODS = Object.freeze(['month', 'year']);

export const PERIOD_LABEL = Object.freeze({
	month: 'per month',
	year: 'per year',
});

/** Anything not recognisably yearly is monthly — the common case, and the one
    that under-states a saving rather than inflating it if wrong. */
export function normalizePeriod(raw) {
	const v = String(raw ?? '').toLowerCase().trim();
	if (v === 'year' || v === 'yearly' || v === 'annual' || v === 'yr' || v === 'y') {
		return 'year';
	}
	return 'month';
}

/**
 * "$69.99", "69.99", " 70 " → number; anything else → null.
 * A leading dollar sign is stripped because that is how people type prices.
 */
export function parseAmount(raw) {
	if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null;
	const cleaned = String(raw ?? '')
		.replace(/[$,\s]/g, '')
		.trim();
	if (!cleaned) return null;
	const n = Number(cleaned);
	return Number.isFinite(n) && n > 0 ? n : null;
}

/** Firestore Timestamp | Date | {_seconds} | ISO string | null → Date | null. */
export function toDate(value) {
	if (!value) return null;
	if (typeof value.toDate === 'function') return value.toDate();
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
	if (typeof value._seconds === 'number') return new Date(value._seconds * 1000);
	if (typeof value === 'string') {
		const d = new Date(value);
		return Number.isNaN(d.getTime()) ? null : d;
	}
	return null;
}

/** One Firestore document → the shape every surface reads. */
export function normalizeSubscription(data, id) {
	const d = data || {};
	return {
		id,
		name: String(d.name ?? '').trim(),
		amount: parseAmount(d.amount) ?? 0,
		period: normalizePeriod(d.period),
		cancelledAt: toDate(d.cancelledAt) || toDate(d.createdAt),
		createdAt: toDate(d.createdAt),
	};
}

/** The only real computation: what one row saves per month. */
export function monthlyEquivalent(sub) {
	if (!sub) return 0;
	const amount = parseAmount(sub.amount) ?? 0;
	return normalizePeriod(sub.period) === 'year' ? amount / 12 : amount;
}

/**
 * The headline figures. `monthly` is the sum of every row's monthly equivalent;
 * `annual` is that times twelve — NOT the sum of yearly amounts plus twelve
 * times the monthly ones computed separately, which would agree today and
 * drift the first time someone rounds one of them.
 */
export function rollup(subs) {
	const list = Array.isArray(subs) ? subs : [];
	const monthly = list.reduce((acc, s) => acc + monthlyEquivalent(s), 0);
	return {
		count: list.length,
		monthly,
		annual: monthly * 12,
	};
}

/** Newest cancellation first; undated rows last; ties by name. */
export function sortSubscriptions(subs) {
	return [...(Array.isArray(subs) ? subs : [])].sort((a, b) => {
		const at = a.cancelledAt ? a.cancelledAt.getTime() : -Infinity;
		const bt = b.cancelledAt ? b.cancelledAt.getTime() : -Infinity;
		if (bt !== at) return bt - at;
		return a.name.localeCompare(b.name);
	});
}

const USD = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

/** 1170.808 → "$1,170.81". Rounds once, here, at the edge. */
export function formatMoney(n) {
	return USD.format(Number.isFinite(n) ? n : 0);
}

/**
 * Validate an entry-row submission. Returns { doc, error } — never throws,
 * because every failure here is a sentence the row needs to show.
 */
export function buildSubscriptionInput({ name, amount, period } = {}) {
	const cleanName = String(name ?? '').trim();
	if (!cleanName) return { doc: null, error: 'Name the subscription.' };
	const cleanAmount = parseAmount(amount);
	if (cleanAmount === null) {
		return { doc: null, error: 'Enter what it cost, as a number above zero.' };
	}
	return {
		doc: { name: cleanName, amount: cleanAmount, period: normalizePeriod(period) },
		error: null,
	};
}
